import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSnackbarStore } from '@/stores/snackbar'
import { i18n } from '@/i18n'
import { canAccessRoute, accessDenialReason } from '@/router/access'

/**
 * `meta.nav` es lo que hace que una ruta aparezca en el menú lateral:
 *
 *   nav: { section, labelKey, icon, order }
 *
 * Sin `nav` la ruta existe pero no se lista (detalles, formularios, legales).
 * El menú se genera a partir de esto (composables/useNavigation.js), así que
 * añadir una sección es añadir una ruta, no tocar también la plantilla del
 * layout como hasta ahora.
 *
 * `meta.layout` decide el ancho del contenido en DefaultLayout:
 *   'default' (por defecto) · 'wide' (a sangre) · 'reading' (texto largo)
 */
const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/pages/HomePage.vue'),
    meta: {
      layout: 'wide',
      titleKey: 'nav.home',
      nav: { section: 'main', labelKey: 'nav.home', icon: 'mdi-home', order: 10 },
    },
  },
  { path: '/login', name: 'login', component: () => import('@/pages/LoginPage.vue') },
  {
    // Destino del enlace del correo de restablecimiento (token y email en la query).
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/pages/ResetPasswordPage.vue'),
    meta: { titleKey: 'resetPassword.title' },
  },
  {
    path: '/add-content',
    name: 'add-content',
    component: () => import('@/pages/AddContentPage.vue'),
    meta: { requiresAuth: true, titleKey: 'nav.add_content' },
  },

  // ── Mi contenido ────────────────────────────────────────────────────────
  {
    path: '/tvshows',
    name: 'tvshows',
    component: () => import('@/pages/TvshowsPage.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'nav.tvshows',
      nav: { section: 'library', labelKey: 'nav.tvshows', icon: 'mdi-television', order: 10 },
    },
  },
  {
    path: '/tvshows/:id',
    name: 'tvshow-detail',
    component: () => import('@/pages/TvshowDetailPage.vue'),
    meta: { layout: 'wide' },
  },
  {
    path: '/movies',
    name: 'movies',
    component: () => import('@/pages/MoviesPage.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'nav.movies',
      nav: { section: 'library', labelKey: 'nav.movies', icon: 'mdi-movie', order: 20 },
    },
  },
  {
    path: '/movies/:id',
    name: 'movie-detail',
    component: () => import('@/pages/MovieDetailPage.vue'),
    meta: { layout: 'wide' },
  },
  {
    path: '/shows',
    name: 'shows',
    component: () => import('@/pages/ShowsPage.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'nav.shows',
      nav: { section: 'library', labelKey: 'nav.shows', icon: 'mdi-theater', order: 30 },
    },
  },
  {
    path: '/shows/event/:source/:id',
    name: 'event-detail',
    component: () => import('@/pages/ShowDetailPage.vue'),
    meta: { layout: 'wide' },
  },
  {
    path: '/shows/:id',
    name: 'show-detail',
    component: () => import('@/pages/ShowDetailPage.vue'),
    meta: { layout: 'wide' },
  },
  {
    path: '/pendientes',
    name: 'pendientes',
    component: () => import('@/pages/PendientesPage.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'nav.pending',
      nav: { section: 'library', labelKey: 'nav.pending', icon: 'mdi-bookmark', order: 40 },
    },
  },
  {
    path: '/lists',
    name: 'lists',
    component: () => import('@/pages/ListsPage.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'nav.my_lists',
      nav: { section: 'library', labelKey: 'nav.my_lists', icon: 'mdi-heart', order: 50 },
    },
  },
  {
    path: '/watch-history',
    name: 'watch-history',
    component: () => import('@/pages/WatchHistoryPage.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'nav.history',
      nav: { section: 'library', labelKey: 'nav.history', icon: 'mdi-history', order: 60 },
    },
  },

  // ── Descubrir ───────────────────────────────────────────────────────────
  {
    path: '/reviews',
    name: 'reviews-public-home',
    component: () => import('@/pages/PublicReviewsTypePage.vue'),
    meta: {
      layout: 'wide',
      titleKey: 'nav.reviews',
      nav: { section: 'discover', labelKey: 'nav.reviews', icon: 'mdi-book-open-variant', order: 10 },
    },
  },
  {
    path: '/reviews/:type',
    name: 'reviews-type',
    component: () => import('@/pages/PublicReviewsTypePage.vue'),
    meta: { layout: 'wide' },
  },
  {
    path: '/reviews/:type/:slug',
    name: 'review-public-detail',
    component: () => import('@/pages/PublicReviewDetailPage.vue'),
    meta: { layout: 'wide' },
  },
  {
    path: '/calendar',
    name: 'calendar',
    component: () => import('@/pages/CalendarPage.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'nav.calendar',
      nav: { section: 'discover', labelKey: 'nav.calendar', icon: 'mdi-calendar', order: 20 },
    },
  },
  {
    path: '/statistics',
    name: 'statistics',
    component: () => import('@/pages/StatisticsPage.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'nav.statistics',
      nav: { section: 'discover', labelKey: 'nav.statistics', icon: 'mdi-chart-bar', order: 30 },
    },
  },
  {
    path: '/achievements',
    name: 'achievements',
    component: () => import('@/pages/AchievementsPage.vue'),
    meta: {
      requiresAuth: true,
      titleKey: 'nav.achievements',
      nav: { section: 'discover', labelKey: 'nav.achievements', icon: 'mdi-trophy', order: 40 },
    },
  },

  // ── Catálogo (dentro de Administración en el menú) ──────────────────────
  {
    path: '/places',
    name: 'places',
    component: () => import('@/pages/PlacesPage.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: 'view places',
      titleKey: 'nav.places',
      nav: { section: 'admin', labelKey: 'nav.places', icon: 'mdi-map-marker', order: 30 },
    },
  },
  {
    path: '/types',
    name: 'types',
    component: () => import('@/pages/TypesPage.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: 'view types',
      titleKey: 'nav.types',
      nav: { section: 'admin', labelKey: 'nav.types', icon: 'mdi-tag', order: 40 },
    },
  },
  {
    path: '/subtypes',
    name: 'subtypes',
    component: () => import('@/pages/SubtypesPage.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: 'view subtypes',
      titleKey: 'nav.subtypes',
      nav: { section: 'admin', labelKey: 'nav.subtypes', icon: 'mdi-tag-multiple', order: 50 },
    },
  },
  {
    path: '/platforms',
    name: 'platforms',
    component: () => import('@/pages/PlatformsPage.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: 'view platforms',
      titleKey: 'nav.platforms',
      nav: { section: 'admin', labelKey: 'nav.platforms', icon: 'mdi-television-box', order: 60 },
    },
  },

  // ── Administración ──────────────────────────────────────────────────────
  {
    path: '/admin/reviews',
    name: 'admin-reviews',
    component: () => import('@/pages/ReviewsPage.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: 'view reviews',
      layout: 'wide',
      titleKey: 'nav.edit_reviews',
      nav: { section: 'admin', labelKey: 'nav.edit_reviews', icon: 'mdi-pencil-box-multiple', order: 5 },
    },
  },
  {
    path: '/admin/reviews/new',
    name: 'admin-review-new',
    component: () => import('@/pages/ReviewFormPage.vue'),
    meta: { requiresAuth: true, requiresPermission: 'create reviews', layout: 'wide' },
  },
  {
    path: '/admin/reviews/:id/edit',
    name: 'admin-review-edit',
    component: () => import('@/pages/ReviewFormPage.vue'),
    meta: { requiresAuth: true, requiresPermission: 'edit reviews', layout: 'wide' },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/pages/UsersPage.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: 'view users',
      titleKey: 'nav.users',
      nav: { section: 'admin', labelKey: 'nav.users', icon: 'mdi-account-group', order: 10 },
    },
  },
  {
    path: '/roles',
    name: 'roles',
    component: () => import('@/pages/RolesPage.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: 'view roles',
      titleKey: 'nav.roles',
      nav: { section: 'admin', labelKey: 'nav.roles', icon: 'mdi-shield-account', order: 20 },
    },
  },

  // ── Fuera del menú ──────────────────────────────────────────────────────
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/pages/NotificationsPage.vue'),
    meta: { requiresAuth: true, titleKey: 'nav.notifications' },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/pages/ProfilePage.vue'),
    meta: { requiresAuth: true, titleKey: 'layout.my_profile' },
  },
  {
    path: '/politica-privacidad',
    name: 'privacy-policy',
    component: () => import('@/pages/PoliticaPrivacidadPage.vue'),
    meta: { layout: 'reading', titleKey: 'footer.privacy' },
  },
  {
    path: '/politica-cookies',
    name: 'cookie-policy',
    component: () => import('@/pages/PoliticaCookiesPage.vue'),
    meta: { layout: 'reading', titleKey: 'footer.cookies' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { titleKey: 'notFoundPage.title' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

// Acceso: misma función que usa el menú, así no pueden divergir.
router.beforeEach((to) => {
  const authStore = useAuthStore()
  const reason = accessDenialReason(to, authStore)
  if (!reason) return true

  // Antes se redirigía a inicio en silencio y parecía que el enlace no hacía
  // nada. Ahora al menos se dice por qué.
  const snackbar = useSnackbarStore()
  snackbar.warning(i18n.global.t(reason === 'unauthenticated' ? 'errors.login_required' : 'errors.no_permission'))
  return { name: 'home' }
})

// Título del documento: hasta ahora se quedaba siempre en el del index.html.
router.afterEach((to) => {
  const key = to.meta?.titleKey
  const base = 'WatchStreak'
  document.title = key ? `${i18n.global.t(key)} · ${base}` : base
})

/** Primer hit: lo envía gtag('config', { send_page_view: true }). Aquí solo SPA. */
let gaInitialNavigationDone = false
router.afterEach((to) => {
  if (!gaInitialNavigationDone) {
    gaInitialNavigationDone = true
    return
  }
  const id = import.meta.env.VITE_GA_MEASUREMENT_ID
  if (!id || typeof window === 'undefined' || typeof window.gtag !== 'function') return
  const loc = `${window.location.origin}${to.fullPath}`
  window.gtag('event', 'page_view', {
    page_location: loc,
    page_path: to.fullPath,
    page_title: to.name || document.title,
  })
})

export { canAccessRoute }
export default router
